// 导入安全AES加密工具
import {
  cryptoJSAesDecrypt as aesSecureDecrypt,
  cryptoJSAesEncrypt as aesSecureEncrypt,
  getSecureRSAPrivateKey,
  getSecureRSAPublicKey
} from './secureAES.js'
import { hextob64, KEYUTIL, KJUR } from 'jsrsasign'
import JSEncrypt from 'jsencrypt'
import { parseBigInt } from 'jsencrypt/lib/lib/jsbn/jsbn'

const CryptoJS = require('crypto-js')

// ⚠️ 已弃用：硬编码密钥存在安全风险，请使用新的安全加密方案
// const VUE_APP_X1A = process.env.VUE_APP_X1A
// const VUE_APP_Y9B = process.env.VUE_APP_Y9B
// const VUE_APP_W7D = decodeBase64(process.env.VUE_APP_W7D)
// const VUE_APP_Z3C = process.env.VUE_APP_Z3C

/**
 * RSA 签名 - 使用安全的密钥获取方式
 * @param {string} content 需要签名的内容
 * @returns {string} 签名后的 Base64 字符串
 */
export function toSign(content) {
  try {
    // 安全获取RSA私钥
    const privateKey = getSecureRSAPrivateKey()
    if (!privateKey) {
      throw new Error('SIGN_KEY 未配置')
    }

    // 获取密钥并初始化签名算法
    const key = KEYUTIL.getKey(privateKey)
    const signature = new KJUR.crypto.Signature({ alg: 'SHA256withRSA' })

    signature.init(key) // 初始化签名对象
    signature.updateString(content) // 更新待签名内容

    const originSign = signature.sign() // 生成签名
    return hextob64(originSign) // 转换为 Base64 格式并返回
  } catch (error) {
    console.error('签名过程中发生错误:', error)
    throw new Error('签名失败') // 抛出错误，调用方可以捕获并处理
  }
}

export function cryptoJSAesEncrypt(str = '', key = null, iv = null) {
  // 如果没有提供自定义密钥，使用安全的加密方案
  if (!key || !iv) {
    return aesSecureEncrypt(str)
  }

  // 向后兼容：如果提供了自定义密钥，使用原有逻辑
  const encrypted = CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(str), CryptoJS.enc.Utf8.parse(key), {
    iv: CryptoJS.enc.Utf8.parse(iv)
  })

  return CryptoJS.enc.Base64.stringify(encrypted.ciphertext)
}

export function cryptoJSAesEncryptWithMode(str = '', key = null, iv = null) {
  // 如果没有提供自定义密钥，使用安全的加密方案
  if (!key || !iv) {
    return aesSecureEncrypt(str)
  }

  // 向后兼容：如果提供了自定义密钥，使用原有逻辑
  const encrypted = CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(str), CryptoJS.enc.Utf8.parse(key), {
    iv: CryptoJS.enc.Utf8.parse(iv),
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  })
  return CryptoJS.enc.Base64.stringify(encrypted.ciphertext)
}

export function cryptoJSAesDecrypt(str = '', key = null, iv = null) {
  // 如果没有提供自定义密钥，使用安全的解密方案
  if (!key || !iv) {
    return aesSecureDecrypt(str)
  }

  // 向后兼容：如果提供了自定义密钥，使用原有逻辑
  if (str) {
    try {
      const decrypt = CryptoJS.AES.decrypt(str, CryptoJS.enc.Utf8.parse(key), {
        iv: CryptoJS.enc.Utf8.parse(iv)
      })
      return CryptoJS.enc.Utf8.stringify(decrypt).toString()
    } catch (error) {
      console.error('AES解密失败:', error)
      return ''
    }
  }
  return ''
}

export function strToAsterisk(str = '') {
  if (!str) {
    return ''
  }

  const strLen = str.length
  if (strLen === 0) {
    return ''
  }

  let strPrefix
  let strSuffix = ''

  if (strLen === 1) {
    strPrefix = str[0]
    strSuffix = str[0]
  }

  if (strLen === 2) {
    strPrefix = str.slice(0, 2)
    strSuffix = str.slice(strLen - 2, strLen)
  }

  if (strLen >= 3) {
    strPrefix = str.slice(0, 3)
    strSuffix = str.slice(strLen - 3, strLen)
  }

  return strPrefix + '***' + strSuffix
}

export function strEmailToAsterisk(str = '') {
  if (!str) {
    return ''
  }
  const strArr = str.split('@')

  if (strArr.length < 2) {
    return ''
  }
  const strStart = strArr[0]
  const strStartlength = strStart.length

  let strPrefix = ''
  if (strStartlength === 1) {
    strPrefix = str[0]
  }

  if (strStartlength >= 2) {
    strPrefix = str.slice(0, 2)
  }

  return strPrefix + '***@' + strArr[1]
}

export function strAesDecryptAndAsterisk(str = '') {
  const decrypt = cryptoJSAesDecrypt(str)
  return strToAsterisk(decrypt)
}

export function strAesDecryptAndEmailAsterisk(str = '') {
  const decrypt = cryptoJSAesDecrypt(str)
  return strEmailToAsterisk(decrypt)
}

export function randomStr(num) {
  const str = [
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
    'R',
    'S',
    'T',
    'U',
    'V',
    'W',
    'X',
    'Y',
    'Z',
    'a',
    'b',
    'c',
    'd',
    'e',
    'f',
    'g',
    'h',
    'i',
    'j',
    'k',
    'l',
    'm',
    'n',
    'o',
    'p',
    'q',
    'r',
    's',
    't',
    'u',
    'v',
    'w',
    'x',
    'y',
    'z'
  ]
  let res = ''
  for (let i = 0; i < num; i++) {
    const id = Math.ceil(Math.random() * 61)
    res += str[id]
  }
  return res
}

export function generateRandomString(length = 16) {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length)
    result += characters[randomIndex]
  }
  return result
}

export function rsaEncrypt(needData) {
  try {
    // 安全获取RSA公钥
    let publicPEM = getSecureRSAPublicKey()
    const encryptor = new JSEncrypt()
    encryptor.setPublicKey(publicPEM) // 设置公钥
    const encrypted = encryptor.encrypt(needData) // 加密数据
    if (!encrypted) {
      throw new Error('加密失败，请检查公钥格式')
    }
    return encrypted // 返回 Base64 编码的加密结果
  } catch (error) {
    console.error('RSA加密失败:', error)
    throw new Error('加密失败')
  }
}

function pkcs1unpad2(d) {
  let b = d.toByteArray()
  let i = 0
  while (i < b.length && b[i] === 0) {
    ++i
  }
  // if (b.length - i !== n - 1 || b[i] !== 2) {
  //   return null
  // }
  ++i
  while (b[i] !== 0) {
    if (++i >= b.length) {
      return null
    }
  }
  var ret = ''
  while (++i < b.length) {
    var c = b[i] & 255
    if (c < 128) {
      // utf-8 decode
      ret += String.fromCharCode(c)
    } else if (c > 191 && c < 224) {
      ret += String.fromCharCode(((c & 31) << 6) | (b[i + 1] & 63))
      ++i
    } else {
      ret += String.fromCharCode(((c & 15) << 12) | ((b[i + 1] & 63) << 6) | (b[i + 2] & 63))
      i += 2
    }
  }
  return ret
}

export function rsaDecrypt(data, publicKey) {
  const encrypt = new JSEncrypt()
  encrypt.setPublicKey(publicKey)
  const rsaKey = encrypt.getKey()
  // 重写decrypt方法
  rsaKey.decrypt = function (ctext) {
    const c = parseBigInt(ctext, 16)
    const m = this.doPublic(c)
    return pkcs1unpad2(m)
  }
  return encrypt.decrypt(data)
}
